// GtDossierView — the candidate dossier as a full page inside the app.
//
// Laid out like the PDF. Three views of the same person:
//   Everything we hold   all of it, internal: contact details, rate, where each
//                        fact came from, pipelines, benches, enrichment sources
//   What a client sees   exactly what a named share or the PDF shows
//   Anonymous            exactly what an anonymous share shows
// The client views are cut down on the server, so this preview cannot show a
// client anything a share would not.
import { useState, useEffect, useCallback } from 'react';
import { useEnrichFetch } from './GtEnrich';
import { downloadDossier } from './gtDossier';

const ORIGIN = { self: 'Candidate', cv: 'CV', enriched: 'Enriched', profile: 'Profile', admin: 'Staff', import: 'Import' };
const MODES = [
  { key: 'internal', label: 'Everything we hold' },
  { key: 'client', label: 'What a client sees' },
  { key: 'anon', label: 'Anonymous' },
];
const LANE = { self_serve: 'Applied themselves', bulk_import: 'CSV import', manual: 'Added by hand', bulk_cv: 'CV upload' };

function ukDate(d) {
  if (!d) return null;
  const x = new Date(d);
  if (isNaN(x.getTime())) return null;
  return String(x.getDate()).padStart(2, '0') + '/' + String(x.getMonth() + 1).padStart(2, '0') + '/' + x.getFullYear();
}
function span(a, b, cur) {
  const s = a ? String(a) : '';
  const e = cur ? 'Present' : (b ? String(b) : '');
  if (s && e) return s === e ? s : s + ' – ' + e;
  return s || e;
}
function initials(name) { return String(name || '').trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'GT'; }
function bare(url) { return String(url || '').replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, ''); }
function originLabel(o) { return ORIGIN[o] || (o ? String(o).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : 'Profile'); }

function Tag({ show, children, tone }) {
  if (!show || !children) return null;
  return <span className={'gtdv-tag' + (tone ? ' gtdv-tag--' + tone : '')}>{children}</span>;
}
function attrText(a) {
  if (!a) return null;
  const conf = a.confidence != null ? Math.round(Number(a.confidence) * 100) + '%' : null;
  return [a.source, conf].filter(Boolean).join(' · ');
}

function Section({ title, aside, children }) {
  return (
    <section className="gtdv-sec">
      <h2><span>{title}</span>{aside}</h2>
      {children}
    </section>
  );
}

export default function GtDossierView({ candidateId, getToken, onClose }) {
  const authFetch = useEnrichFetch(getToken);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('internal');
  const [pdfBusy, setPdfBusy] = useState(null);

  const load = useCallback(async () => {
    try { setData(await authFetch('/api/candidates/' + encodeURIComponent(candidateId) + '/dossier')); }
    catch (e) { setError(e.message || 'Could not load the dossier.'); }
  }, [authFetch, candidateId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const keep = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = keep; };
  }, [onClose]);

  async function pdf(anonymous) {
    setPdfBusy(anonymous ? 'anon' : 'named');
    try {
      const v = data && data.internal;
      await downloadDossier(getToken, candidateId, { name: v && v.name, refLabel: v && v.ref, anonymous });
    } catch (e) { setError(e.message); }
    setPdfBusy(null);
  }

  const v = data ? (mode === 'internal' ? data.internal : mode === 'client' ? data.client_view : data.client_view_anonymous) : null;
  const internal = mode === 'internal';
  const meta = internal && data ? data.internal.meta || {} : {};
  const attr = meta.attribution || {};
  const tagFor = field => <Tag show={internal}>{attrText(attr[field])}</Tag>;

  return (
    <div className="gtdv-shell" role="dialog" aria-modal="true" aria-label="Candidate dossier">
      <div className="gtdv-bar">
        <div className="gtdv-bar-in">
          <button type="button" className="gtdv-close" onClick={onClose} aria-label="Close the dossier">←</button>
          <div className="gtdv-bar-title">
            <b>{v ? v.displayName : 'Dossier'}</b>
            <span>Candidate dossier</span>
          </div>
          <div className="gtdv-modes" role="tablist" aria-label="Who is looking">
            {MODES.map(m => (
              <button key={m.key} type="button" role="tab" aria-selected={mode === m.key}
                className={'gtdv-mode' + (mode === m.key ? ' gtdv-mode--on' : '')} onClick={() => setMode(m.key)}>{m.label}</button>
            ))}
          </div>
          <div className="gtdv-bar-acts">
            <button type="button" className="gtc-btn-ghost" disabled={!!pdfBusy || !data} onClick={() => pdf(false)}>{pdfBusy === 'named' ? 'Making PDF…' : 'Download PDF'}</button>
            <button type="button" className="gtc-btn-ghost" disabled={!!pdfBusy || !data} onClick={() => pdf(true)}>{pdfBusy === 'anon' ? 'Making PDF…' : 'Anonymous PDF'}</button>
          </div>
        </div>
      </div>

      <div className="gtdv-scroll">
        {error && <div className="gtdv-error">{error}</div>}
        {!data && !error && <div className="gtdv-loading">Building the dossier…</div>}
        {v && (
          <article className="gtdv-sheet">
            {!internal && (
              <div className="gtdv-note">
                {mode === 'client'
                  ? 'This is what a client sees on a named share and in the downloaded PDF. Contact details and rate stay hidden unless you switch them on for a share.'
                  : 'This is what a client sees on an anonymous share: no name, links or publications, and identifying details removed from the text.'}
              </div>
            )}
            <header className="gtdv-head">
              <div className="gtdv-brand"><b>Generative Talent Co</b><span>Candidate dossier · Confidential</span></div>
              <div className="gtdv-hero">
                <span className={'gtdv-mono' + (v.anonymous ? ' gtdv-mono--anon' : '')}>{v.anonymous ? 'GT' : initials(v.name)}</span>
                <div className="gtdv-hero-text">
                  <h1>{v.displayName}</h1>
                  {(v.currentTitle || v.currentCompany) && (
                    <p className="gtdv-role">{[v.currentTitle, v.currentCompany].filter(Boolean).join('  ·  ')} {tagFor('current_title')}</p>
                  )}
                  {v.headline && <p className="gtdv-headline">{v.headline} {tagFor('headline')}</p>}
                </div>
              </div>
              <dl className="gtdv-facts">
                {[
                  ['Location', v.location, 'location'], ['Availability', v.availability], ['Right to work', v.rightToWork],
                  ['Rate expectation', v.rate], ['Expertise', v.domain], ['Reference', v.ref],
                ].filter(f => f[1]).map(f => (
                  <div key={f[0]}><dt>{f[0]}</dt><dd>{f[1]} {f[2] ? tagFor(f[2]) : null}</dd></div>
                ))}
                {internal && v.contact && v.contact.email && <div><dt>Email</dt><dd><a href={'mailto:' + v.contact.email}>{v.contact.email}</a></dd></div>}
                {internal && v.contact && v.contact.phone && <div><dt>Phone</dt><dd>{v.contact.phone}</dd></div>}
                {internal && v.contact && v.contact.linkedin && <div><dt>LinkedIn</dt><dd><a href={v.contact.linkedin} target="_blank" rel="noreferrer">{bare(v.contact.linkedin).replace(/^linkedin\.com\/in\//, '')}</a> {tagFor('linkedin_url')}</dd></div>}
                {internal && meta.status && <div><dt>Status</dt><dd>{String(meta.status).replace(/_/g, ' ')}</dd></div>}
                {internal && meta.intakeLane && <div><dt>How they came in</dt><dd>{LANE[meta.intakeLane] || meta.intakeLane}</dd></div>}
                {internal && <div><dt>Consent</dt><dd>{meta.consent === 'explicit' ? 'Given' : meta.consent === 'withdrawn' ? 'Withdrawn' : 'Not yet asked'}</dd></div>}
                {internal && <div><dt>Last enriched</dt><dd>{ukDate(meta.lastEnrichedAt) || 'Never'}</dd></div>}
                {internal && meta.owner && <div><dt>Owner</dt><dd>{meta.owner}</dd></div>}
              </dl>
            </header>

            {v.summary && (
              <Section title="Profile" aside={tagFor('profile_summary')}>
                <p className="gtdv-prose">{v.summary}</p>
              </Section>
            )}

            {v.assessment && (() => {
              const A = v.assessment;
              const BANDS = { strong: ['Strong match', 'green'], possible: ['Possible match', 'amber'], weak: ['Weaker match', 'slate'], incomplete: ['Partly assessed', 'slate'] };
              const pct = A.overallPct == null ? null : Math.max(0, Math.min(100, Math.round(A.overallPct)));
              const b = BANDS[A.band] || null;
              const tone = b ? b[1] : (pct == null ? 'slate' : pct >= 75 ? 'green' : pct >= 55 ? 'amber' : 'slate');
              const fmt = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));
              return (
                <Section title="Our assessment">
                  <p className="gtdv-sub">{['Structured interview', A.roleTitle ? 'for ' + A.roleTitle : null, ukDate(A.completedAt)].filter(Boolean).join(' · ')}</p>
                  {(pct != null || A.summary || b) && (
                    <div className="gtps-ax-hero">
                      {pct != null && (
                        <div className={'gtps-ax-ring gtps-ax-ring--' + tone} style={{ '--p': pct }} aria-label={pct + '% overall'}>
                          <span><b>{pct}%</b><em>overall</em></span>
                        </div>
                      )}
                      <div className="gtps-ax-hero-t">
                        <div className="gtps-ax-tags">
                          {b && <span className={'gtps-ax-band gtps-ax-band--' + tone}>{b[0]}</span>}
                          {A.total ? <span className="gtps-ax-cov">{A.covered} of {A.total} areas covered</span> : null}
                        </div>
                        {A.summary && <p className="gtps-ax-sum">{A.summary}</p>}
                      </div>
                    </div>
                  )}
                  {A.strengths && A.strengths.length > 0 && (
                    <ul className="gtps-ax-str">{A.strengths.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  )}
                  <div className="gtdv-ass">
                    {A.criteria.map((c, i) => {
                      const p = Math.max(0, Math.min(1, c.score / c.max));
                      const band = p >= 0.75 ? 'green' : p >= 0.5 ? 'amber' : 'slate';
                      return (
                        <div key={i} className="gtdv-ass-row">
                          <div className="gtdv-ass-head">
                            <b>{c.criterion}{c.weight === 3 && <em className="gtps-ax-key">Key requirement</em>}</b>
                            <span className="gtdv-bar-track"><span className={'gtdv-bar-fill gtdv-' + band} style={{ width: Math.max(4, p * 100) + '%' }} /></span>
                            <span className="gtdv-score">{fmt(c.score)} / {c.max}</span>
                          </div>
                          {c.evidence.map((e, j) => <blockquote key={j}>“{e}”</blockquote>)}
                        </div>
                      );
                    })}
                  </div>
                  {A.notCovered && A.notCovered.length > 0 && <p className="gtps-ax-nc">Not covered in the interview: {A.notCovered.join(', ')}.</p>}
                </Section>
              );
            })()}

            {v.clips && v.clips.length > 0 && (
              <Section title="In their own words">
                <p className="gtdv-sub">{v.clips.length === 1 ? 'One clip goes' : v.clips.length + ' clips go'} on named profile links where Interview clips is on. Play or edit them on the interview's Recording and transcript.</p>
                <div className="gtdv-clips">
                  {v.clips.map((c) => (
                    <div key={c.id} className="gtdv-clip">
                      <span className="gtdv-clip-ico" aria-hidden="true">▶</span>
                      <b>{c.title}</b>
                      <span>{Math.floor((c.duration_s || 0) / 60) + ':' + String(Math.round(c.duration_s || 0) % 60).padStart(2, '0')}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {v.experience.length > 0 && (
              <Section title="Experience" aside={tagFor('experience')}>
                <ol className="gtdv-tl">
                  {v.experience.map((e, i) => (
                    <li key={i}>
                      <span className="gtdv-when">{span(e.start, e.end, e.current)}</span>
                      <div className="gtdv-what">
                        <b>{e.title || e.company}</b>
                        {(e.title && e.company) || e.place ? <span className="gtdv-org">{[e.title ? e.company : null, e.place].filter(Boolean).join('  ·  ')}</span> : null}
                        {e.description && <div className="gtdv-desc">{e.description.split(/\n+/).map((l, k) => <p key={k}>{l}</p>)}</div>}
                        <Tag show={internal} tone={e.origin === 'enriched' ? 'teal' : null}>{originLabel(e.origin)}</Tag>
                      </div>
                    </li>
                  ))}
                </ol>
              </Section>
            )}

            {v.education.length > 0 && (
              <Section title="Education" aside={tagFor('education')}>
                <ol className="gtdv-tl">
                  {v.education.map((e, i) => (
                    <li key={i}>
                      <span className="gtdv-when">{span(e.start, e.end, false)}</span>
                      <div className="gtdv-what">
                        <b>{[e.degree, e.field].filter(Boolean).join(', ') || e.school}</b>
                        {(e.degree || e.field) && e.school && <span className="gtdv-org">{e.school}</span>}
                        <Tag show={internal} tone={e.origin === 'enriched' ? 'teal' : null}>{originLabel(e.origin)}</Tag>
                      </div>
                    </li>
                  ))}
                </ol>
              </Section>
            )}

            {v.skills.length > 0 && (
              <Section title="Skills" aside={tagFor('skills')}>
                <div className="gtdv-pills">
                  {v.skills.map((s, i) => {
                    const o = internal && data.internal.skillOrigins ? data.internal.skillOrigins[String(s).toLowerCase()] : null;
                    return <span key={i} className={'gtdv-pill' + (o === 'enriched' ? ' gtdv-pill--enriched' : '')} title={internal && o ? 'From ' + originLabel(o) : undefined}>{s}</span>;
                  })}
                </div>
                {internal && <p className="gtdv-key"><span className="gtdv-pill gtdv-pill--enriched">Skill</span> found by enrichment</p>}
              </Section>
            )}

            {[
              ['Projects', 'projects', p => ({ title: p.name, sub: null, body: p.description, url: p.url })],
              ['Certifications', 'certifications', x => ({ when: x.year, title: x.name, sub: x.issuer, url: x.url })],
              ['Publications', 'publications', p => ({ when: p.year, title: p.title, sub: p.publisher, url: p.url })],
              ['Awards', 'awards', a => ({ when: a.year, title: a.title, sub: a.issuer, body: a.description })],
            ].map(([title, key, map]) => (v[key] && v[key].length ? (
              <Section key={key} title={title} aside={tagFor(key)}>
                <ol className="gtdv-tl">
                  {v[key].map((it, i) => {
                    const r = map(it);
                    return (
                      <li key={i}>
                        <span className="gtdv-when">{r.when || ''}</span>
                        <div className="gtdv-what">
                          <b>{r.title}</b>
                          {r.sub && <span className="gtdv-org">{r.sub}</span>}
                          {r.body && <div className="gtdv-desc"><p>{r.body}</p></div>}
                          {r.url && <a className="gtdv-link" href={r.url} target="_blank" rel="noreferrer">{bare(r.url)}</a>}
                          <Tag show={internal} tone={it.origin === 'enriched' ? 'teal' : null}>{originLabel(it.origin)}</Tag>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </Section>
            ) : null))}

            {v.languages.length > 0 && (
              <Section title="Languages">
                <div className="gtdv-pills">{v.languages.map((l, i) => <span key={i} className="gtdv-pill">{l.language}{l.proficiency ? ' · ' + l.proficiency : ''}</span>)}</div>
              </Section>
            )}

            {v.links.length > 0 && (
              <Section title="Links">
                <ul className="gtdv-links">
                  {v.links.map((l, i) => (
                    <li key={i}><span>{l.label || l.kind || 'Link'}</span><a href={/^https?:\/\//i.test(l.value) ? l.value : 'https://' + l.value} target="_blank" rel="noreferrer">{bare(l.value)}</a></li>
                  ))}
                </ul>
              </Section>
            )}

            {internal && (
              <div className="gtdv-internal">
                <div className="gtdv-internal-h">Only you can see this</div>
                <div className="gtdv-cols">
                  <div>
                    <h3>Roles</h3>
                    {meta.roles && meta.roles.length ? (
                      <ul className="gtdv-list">{meta.roles.map((r, i) => <li key={i}><b>{r.title}</b><span>{[r.client, r.stage, r.status !== 'open' ? r.status : null].filter(Boolean).join(' · ')}</span></li>)}</ul>
                    ) : <p className="gtdv-muted">Not on any role.</p>}
                  </div>
                  <div>
                    <h3>Benches</h3>
                    {meta.benches && meta.benches.length ? (
                      <ul className="gtdv-list">{meta.benches.map((b, i) => <li key={i}><b>{b.name}</b><span>{b.bucket || 'No bucket'}</span></li>)}</ul>
                    ) : <p className="gtdv-muted">Not on any bench.</p>}
                  </div>
                  <div>
                    <h3>Enrichment sources</h3>
                    {meta.enrichment && meta.enrichment.sources.length ? (
                      <ul className="gtdv-list">
                        {meta.enrichment.sources.map((s, i) => (
                          <li key={i}><b>{s.kind}</b><a href={s.url} target="_blank" rel="noreferrer" title={s.url}>{s.title || bare(s.url)}</a></li>
                        ))}
                      </ul>
                    ) : <p className="gtdv-muted">{meta.lastEnrichedAt ? 'No web sources were used.' : 'Not enriched yet.'}</p>}
                  </div>
                  <div>
                    <h3>Files</h3>
                    <p className="gtdv-muted">{meta.cvOnFile ? 'Their original CV is on file.' : 'No CV on file.'} Added {ukDate(meta.createdAt) || 'on an unknown date'}.</p>
                  </div>
                </div>
              </div>
            )}

            <footer className="gtdv-foot">Shared in confidence by Generative Talent Co. Please do not forward. · {v.ref}</footer>
          </article>
        )}
      </div>
    </div>
  );
}
